import axios from 'axios';
import { API_ROOT } from '@/lib/config';

// Resolution and the missing-configuration warning both live in lib/config,
// so there is exactly one place that decides where the API is.
const API = API_ROOT;

// Create axios instance with resilience defaults
const apiInstance = axios.create({
  baseURL: API,
  timeout: 15000, // 15 seconds for regular requests
  withCredentials: true, // Send httpOnly cookies with requests (needed for refresh token)
});

// Token refresh queue to prevent multiple simultaneous refresh calls
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (resolve, reject) => {
  refreshSubscribers.push({ resolve, reject });
};

const onRefreshed = (token) => {
  refreshSubscribers.forEach(({ resolve }) => resolve(token));
  refreshSubscribers = [];
};

const onRefreshFailed = (error) => {
  refreshSubscribers.forEach(({ reject }) => reject(error));
  refreshSubscribers = [];
};

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  delays: [1000, 2000, 4000], // exponential backoff: 1s, 2s, 4s
  retryableStatuses: [408, 429, 500, 502, 503, 504],
  retryableMethods: ['GET', 'HEAD', 'OPTIONS'], // Only retry idempotent methods by default
};

// Check if error is retryable
const isRetryableError = (error, retryCount) => {
  if (retryCount >= RETRY_CONFIG.maxRetries) {
    return false;
  }

  // No network error or timeout
  if (!error.response && !error.code) {
    return false;
  }

  // Network errors and timeouts are retryable
  if (error.code === 'ECONNABORTED' || error.code === 'ENOTFOUND' || error.code === 'ECONNREFUSED') {
    return true;
  }

  // Retry on specific HTTP status codes
  if (error.response && RETRY_CONFIG.retryableStatuses.includes(error.response.status)) {
    return true;
  }

  return false;
};

// Sleep utility for backoff
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Add auth token and retry count to requests
apiInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Initialize retry count if not already set
  if (!config.retryCount) {
    config.retryCount = 0;
  }

  // Add retry count header for debugging
  config.headers['X-Retry-Count'] = config.retryCount;

  // Set longer timeout for upload endpoints
  if (config.url?.includes('/upload/')) {
    config.timeout = 30000; // 30 seconds for uploads
  }

  return config;
});

// Response interceptor with retry and token refresh logic
apiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const config = error.config;

    // Handle 401 - attempt token refresh
    if (error.response?.status === 401 && config) {
      if (!isRefreshing) {
        isRefreshing = true;
        const refreshToken = localStorage.getItem('refresh_token');

        if (refreshToken) {
          // Attempt to refresh the token — cookie carries the refresh token,
          // but also send in body for backward compatibility during migration
          axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken }, {
            timeout: 15000,
            withCredentials: true,
            validateStatus: () => true // Don't throw on any status
          })
            .then((response) => {
              // Backend refresh returns { access_token, refresh_token, token_type }
              const newToken = response.data?.access_token || response.data?.token;
              if (response.status === 200 && newToken) {
                localStorage.setItem('token', newToken);
                // Also rotate the refresh token if a new one was issued
                if (response.data.refresh_token) {
                  localStorage.setItem('refresh_token', response.data.refresh_token);
                }
                apiInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                onRefreshed(newToken);
                isRefreshing = false;
              } else {
                // Refresh failed - reject queued requests, clear auth, redirect
                const refreshError = new Error('Token refresh failed');
                onRefreshFailed(refreshError);
                localStorage.removeItem('token');
                localStorage.removeItem('refresh_token');
                delete apiInstance.defaults.headers.common['Authorization'];
                isRefreshing = false;
                if (window.location.pathname !== '/auth' && !window.location.pathname.startsWith('/advertiser')) {
                  window.location.href = '/auth';
                }
              }
            })
            .catch((err) => {
              // Refresh error - reject queued requests, clear auth, redirect
              onRefreshFailed(err);
              localStorage.removeItem('token');
              localStorage.removeItem('refresh_token');
              delete apiInstance.defaults.headers.common['Authorization'];
              isRefreshing = false;
              if (window.location.pathname !== '/auth' && !window.location.pathname.startsWith('/advertiser')) {
                window.location.href = '/auth';
              }
            });
        } else {
          // No refresh token - reject queued requests, clear auth, redirect
          const noTokenError = new Error('No refresh token available');
          onRefreshFailed(noTokenError);
          localStorage.removeItem('token');
          delete apiInstance.defaults.headers.common['Authorization'];
          isRefreshing = false;
          if (window.location.pathname !== '/auth' && !window.location.pathname.startsWith('/advertiser')) {
            window.location.href = '/auth';
          }
        }
      }

      // Queue this request to retry after token refresh (or reject if refresh fails)
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh(
          (token) => {
            config.headers.Authorization = `Bearer ${token}`;
            resolve(apiInstance(config));
          },
          (err) => {
            reject(err);
          }
        );
      });
    }

    // Handle retryable errors with exponential backoff
    if (isRetryableError(error, config?.retryCount || 0)) {
      const retryCount = (config.retryCount || 0) + 1;
      const delayMs = RETRY_CONFIG.delays[retryCount - 1];

      // Only retry GET and other idempotent methods
      if (RETRY_CONFIG.retryableMethods.includes(config.method?.toUpperCase())) {
        config.retryCount = retryCount;

        return sleep(delayMs).then(() => {
          console.warn(`Retrying request (attempt ${retryCount}/${RETRY_CONFIG.maxRetries}): ${config.method?.toUpperCase()} ${config.url}`, {
            originalError: error.code || error.response?.status,
          });
          return apiInstance(config);
        });
      }
    }

    return Promise.reject(error);
  }
);

// AbortController registry for component unmounts
const pendingRequests = new Map();

apiInstance.interceptors.request.use((config) => {
  const controller = new AbortController();
  config.signal = controller.signal;
  const requestKey = `${config.method}_${config.url}_${Date.now()}`;
  pendingRequests.set(requestKey, controller);
  return config;
});

// Cancel pending requests
export const cancelPendingRequests = () => {
  pendingRequests.forEach((controller) => {
    controller.abort();
  });
  pendingRequests.clear();
};

// Default export for direct api calls
export default apiInstance;

// Posts (authenticated)
export const createPost = (data) => apiInstance.post('/posts', data);
export const getPost = (id) => apiInstance.get(`/posts/${id}`);
export const deletePost = (id) => apiInstance.delete(`/posts/${id}`);
export const getFeed = (limit = 20) => apiInstance.get(`/feed?limit=${limit}`);
export const getDiscover = (limit = 20) => apiInstance.get(`/discover?limit=${limit}`);

// Feed v2 with cursor pagination (Phase 3)
export const getFeedV2 = (options = {}) => {
  const { cursor, limit = 20, mode = 'chronological' } = options;
  const params = new URLSearchParams();
  params.append('limit', limit);
  params.append('mode', mode);
  if (cursor) params.append('cursor', cursor);
  return apiInstance.get(`/feed/v2?${params.toString()}`);
};

// Feed timeline status
export const getFeedTimelineStatus = () => apiInstance.get('/feed/timeline/status');
export const rebuildTimeline = () => apiInstance.post('/feed/timeline/rebuild');
export const getFeedHealth = () => apiInstance.get('/feed/health');

// Users
export const getUser = (username) => apiInstance.get(`/users/${username}`);
export const getMyVolunteering = () => apiInstance.get('/my-volunteering');
export const getUserPosts = (userId) => apiInstance.get(`/users/${userId}/posts`);
export const updateProfile = (data) => apiInstance.put('/users/profile', data);
export const searchUsers = (q) => apiInstance.get(`/search/users?q=${q}`);

// File Upload
export const uploadImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return apiInstance.post('/upload/image', formData, {
    headers: { 
      'Content-Type': 'multipart/form-data'
    }
  });
};

// Avatar & Cover Upload (S3 persistent storage)
export const uploadAvatar = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return apiInstance.post('/upload/avatar', formData, {
    headers: { 
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const uploadCover = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return apiInstance.post('/upload/cover', formData, {
    headers: { 
      'Content-Type': 'multipart/form-data'
    }
  });
};

// Storage status check
export const getStorageStatus = () => apiInstance.get('/storage/status');

// Uplifts
export const getUplifts = () => apiInstance.get('/uplifts');

// Follow/Connections
export const followUser = (userId, connectionType = 'friend') => 
  apiInstance.post(`/users/${userId}/follow?connection_type=${connectionType}`);
export const unfollowUser = (userId) => apiInstance.delete(`/users/${userId}/follow`);
export const updateConnectionType = (userId, connectionType) => 
  apiInstance.put(`/users/${userId}/connection`, { connection_type: connectionType });
export const getConnectionTypes = () => apiInstance.get('/connections/types');
export const getConnectionsByType = (type) => apiInstance.get(`/connections/by-type/${type}`);
export const getConnectionsFeed = (connectionType = null, limit = 30) => {
  const params = new URLSearchParams();
  if (connectionType) params.append('connection_type', connectionType);
  params.append('limit', limit);
  return apiInstance.get(`/connections/feed?${params.toString()}`);
};
export const getFollowers = (userId) => apiInstance.get(`/users/${userId}/followers`);
export const getFollowing = (userId) => apiInstance.get(`/users/${userId}/following`);

// Ad Preferences
export const getAdPreferences = () => apiInstance.get('/users/preferences/ads');
export const updateAdPreferences = (data) => apiInstance.put('/users/preferences/ads', data);

// Content Filters
export const getContentFilters = () => apiInstance.get('/users/preferences/filters');
export const updateContentFilters = (data) => apiInstance.put('/users/preferences/filters', data);

// Likes (with attribution params for data lake)
export const likePost = (postId, attribution = {}) => {
  const params = new URLSearchParams();
  if (attribution.impressionId) params.append('impression_id', attribution.impressionId);
  if (attribution.position !== undefined) params.append('position', attribution.position);
  if (attribution.feedMode) params.append('feed_mode', attribution.feedMode);
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiInstance.post(`/posts/${postId}/like${query}`);
};

export const unlikePost = (postId, attribution = {}) => {
  const params = new URLSearchParams();
  if (attribution.impressionId) params.append('impression_id', attribution.impressionId);
  if (attribution.position !== undefined) params.append('position', attribution.position);
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiInstance.delete(`/posts/${postId}/like${query}`);
};

// Comments (with attribution params for data lake)
export const getComments = (postId) => apiInstance.get(`/posts/${postId}/comments`);
export const createComment = (postId, content, attribution = {}) => {
  const params = new URLSearchParams();
  if (attribution.impressionId) params.append('impression_id', attribution.impressionId);
  if (attribution.position !== undefined) params.append('position', attribution.position);
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiInstance.post(`/posts/${postId}/comments${query}`, { content });
};
export const deleteComment = (postId, commentId) => apiInstance.delete(`/posts/${postId}/comments/${commentId}`);

// Messages
export const getConversations = () => apiInstance.get('/conversations');
export const createConversation = (userId) => apiInstance.post(`/conversations/${userId}`);
export const getMessages = (convId) => apiInstance.get(`/conversations/${convId}/messages`);
export const sendMessage = (convId, data) => apiInstance.post(`/conversations/${convId}/messages`, data);

// Spaces
export const getMySpaces = () => apiInstance.get('/spaces');
export const discoverSpaces = () => apiInstance.get('/spaces/discover');
export const getSpace = (spaceId) => apiInstance.get(`/spaces/${spaceId}`);
export const createSpace = (data) => apiInstance.post('/spaces', data);
export const updateSpace = (spaceId, data) => apiInstance.put(`/spaces/${spaceId}`, data);
export const deleteSpace = (spaceId) => apiInstance.delete(`/spaces/${spaceId}`);
export const joinSpace = (spaceId) => apiInstance.post(`/spaces/${spaceId}/join`);
export const leaveSpace = (spaceId) => apiInstance.post(`/spaces/${spaceId}/leave`);
export const updateSpaceSettings = (spaceId, data) => apiInstance.put(`/spaces/${spaceId}/settings`, data);

// Space Join Requests
export const requestToJoinSpace = (spaceId, data) => apiInstance.post(`/spaces/${spaceId}/request-join`, data);
export const getJoinRequests = (spaceId) => apiInstance.get(`/spaces/${spaceId}/join-requests`);
export const approveJoinRequest = (spaceId, requestId) => 
  apiInstance.post(`/spaces/${spaceId}/join-requests/${requestId}/approve`);
export const rejectJoinRequest = (spaceId, requestId) => 
  apiInstance.post(`/spaces/${spaceId}/join-requests/${requestId}/reject`);

// Space Invitations
export const inviteToSpace = (spaceId, data) => apiInstance.post(`/spaces/${spaceId}/invite`, data);
export const getSpaceInvitations = (spaceId) => apiInstance.get(`/spaces/${spaceId}/invitations`);
export const cancelInvitation = (spaceId, invitationId) => 
  apiInstance.delete(`/spaces/${spaceId}/invitations/${invitationId}`);

// User Invitations
export const getMyInvitations = () => apiInstance.get('/user/invitations');
export const acceptInvitation = (invitationId) => apiInstance.post(`/user/invitations/${invitationId}/accept`);
export const declineInvitation = (invitationId) => apiInstance.post(`/user/invitations/${invitationId}/decline`);

// Space Posts
export const getSpacePosts = (spaceId) => apiInstance.get(`/spaces/${spaceId}/posts`);
export const createSpacePost = (spaceId, data) => apiInstance.post(`/spaces/${spaceId}/posts`, data);

// Space Members
export const getSpaceMembers = (spaceId) => apiInstance.get(`/spaces/${spaceId}/members`);
export const getSpaceMembersDetailed = (spaceId) => apiInstance.get(`/spaces/${spaceId}/members/detailed`);
export const updateMemberRole = (spaceId, userId, role) => 
  apiInstance.put(`/spaces/${spaceId}/members/${userId}/role`, { role });
export const removeMember = (spaceId, userId) => 
  apiInstance.delete(`/spaces/${spaceId}/members/${userId}`);

// Space Events
export const getSpaceEvents = (spaceId) => apiInstance.get(`/spaces/${spaceId}/events`);
export const createSpaceEvent = (spaceId, data) => apiInstance.post(`/spaces/${spaceId}/events`, data);
export const deleteSpaceEvent = (spaceId, eventId) => 
  apiInstance.delete(`/spaces/${spaceId}/events/${eventId}`);
export const attendEvent = (spaceId, eventId, status = 'going') => 
  apiInstance.post(`/spaces/${spaceId}/events/${eventId}/attend?status=${status}`);

// Search
export const search = (params) => apiInstance.get('/search', { params });
export const searchSuggest = (q, limit = 5) => apiInstance.get('/search/suggest', { params: { q, limit } });
export const searchTrending = (type, limit = 10) => apiInstance.get('/search/trending', { params: { type, limit } });
export const searchHealth = () => apiInstance.get('/search/health');
export const searchReindex = () => apiInstance.post('/search/reindex');
export const searchStats = () => apiInstance.get('/search/stats');

// Tips
export const sendTip = (data) => apiInstance.post('/tips/send', data);
export const getTipBalance = () => apiInstance.get('/tips/my/balance');
export const getMyReceivedTips = (limit = 50) => apiInstance.get(`/tips/my/received?limit=${limit}`);
export const getMySentTips = (limit = 50) => apiInstance.get(`/tips/my/sent?limit=${limit}`);
export const getUserTips = (userId, limit = 20) => apiInstance.get(`/tips/user/${userId}?limit=${limit}`);
export const getUserTopSupporters = (userId, limit = 10) => apiInstance.get(`/tips/user/${userId}/top-supporters?limit=${limit}`);
export const getUserTipStats = (userId) => apiInstance.get(`/tips/user/${userId}/stats`);

// Briefs (Twitter-like microblog)
export const getBriefs = (cursor = null, limit = 20) => {
  const params = new URLSearchParams();
  params.append('limit', limit);
  if (cursor) params.append('cursor', cursor);
  return apiInstance.get(`/briefs?${params.toString()}`);
};
export const createBrief = (content, image_url = null) => apiInstance.post('/briefs', { content, image_url });
export const getBrief = (briefId) => apiInstance.get(`/briefs/${briefId}`);
export const deleteBrief = (briefId) => apiInstance.delete(`/briefs/${briefId}`);
export const likeBrief = (briefId) => apiInstance.post(`/briefs/${briefId}/like`);
export const unlikeBrief = (briefId) => apiInstance.delete(`/briefs/${briefId}/like`);
export const getBriefComments = (briefId, limit = 50) => apiInstance.get(`/briefs/${briefId}/comments?limit=${limit}`);
export const createBriefComment = (briefId, content) => apiInstance.post(`/briefs/${briefId}/comments`, { content });
export const deleteBriefComment = (briefId, commentId) => apiInstance.delete(`/briefs/${briefId}/comments/${commentId}`);

// Password Reset
export const forgotPassword = (email) => apiInstance.post('/auth/forgot-password', { email });
export const resetPassword = (token, new_password) => apiInstance.post('/auth/reset-password', { token, new_password });

// Impact - Proof of Impact. Read-only: Impact is earned by doing things
// elsewhere in FamCentral, never by calling an endpoint.
export const getMyImpact = () => apiInstance.get('/impact/me');
export const getMyImpactEntries = (limit = 30, skip = 0, period = null) => {
  const params = new URLSearchParams();
  params.append('limit', limit);
  params.append('skip', skip);
  if (period) params.append('period', period);
  return apiInstance.get(`/impact/me/entries?${params.toString()}`);
};
export const getImpactBreakdown = (period = 'lifetime', key = null) => {
  const params = new URLSearchParams();
  params.append('period', period);
  if (key) params.append('key', key);
  return apiInstance.get(`/impact/me/breakdown?${params.toString()}`);
};
export const getMyRipples = () => apiInstance.get('/impact/me/ripples');
export const getImpactSchedule = () => apiInstance.get('/impact/schedule');

// -- Businesses --------------------------------------------------------------
//
// A business is owned by the member who created it, so these all travel on the
// ordinary member token. Nothing here needs a second login.

export const discoverBusinesses = (params = {}) => {
  const query = new URLSearchParams();
  if (params.category) query.set('category', params.category);
  if (params.radiusMiles) query.set('radius_miles', params.radiusMiles);
  if (params.limit) query.set('limit', params.limit);
  const qs = query.toString();
  return apiInstance.get(`/businesses${qs ? `?${qs}` : ''}`);
};

export const getMyBusinesses = () => apiInstance.get('/businesses/mine');
export const createBusiness = (data) => apiInstance.post('/businesses', data);
export const getBusinessBySlug = (slug) => apiInstance.get(`/businesses/${slug}`);

export const getBusinessForEditing = (id) => apiInstance.get(`/businesses/${id}/manage`);
export const updateBusiness = (id, data) => apiInstance.patch(`/businesses/${id}`, data);
export const setBusinessLocation = (id, data) => apiInstance.put(`/businesses/${id}/location`, data);
export const publishBusiness = (id) => apiInstance.post(`/businesses/${id}/publish`);
export const unpublishBusiness = (id) => apiInstance.post(`/businesses/${id}/unpublish`);

// The member's own location, which is what "nearest first" measures from.
export const setMyLocation = (data) => apiInstance.put('/users/me/location', data);
export const getMyLocation = () => apiInstance.get('/users/me/location');
export const clearMyLocation = () => apiInstance.delete('/users/me/location');

// Closing an account. Two steps: find out what it would do, then do it.
export const previewAccountDeletion = () => apiInstance.get('/users/me/deletion');
export const deleteMyAccount = (data) => apiInstance.delete('/users/me', { data });

// The FamCentral Business subscription. Every one of these answers plainly
// while charging is switched off, rather than half-working.
export const getSubscription = (id) => apiInstance.get(`/businesses/${id}/subscription`);
export const startSubscription = (id, originUrl) =>
  apiInstance.post(`/businesses/${id}/subscription/checkout?origin_url=${encodeURIComponent(originUrl)}`);
export const cancelSubscription = (id) => apiInstance.post(`/businesses/${id}/subscription/cancel`);
export const resumeSubscription = (id) => apiInstance.post(`/businesses/${id}/subscription/resume`);
export const openBillingPortal = (id, returnUrl) =>
  apiInstance.post(`/businesses/${id}/subscription/portal?return_url=${encodeURIComponent(returnUrl)}`);

// The shop. No price is ever sent from here - a basket is what you want and
// how many, and the server works out what it costs.
export const getShopProducts = (slug) => apiInstance.get(`/shop/${slug}/products`);
export const priceBasket = (businessId, lines) =>
  apiInstance.post('/shop/basket/price', { business_id: businessId, lines });
export const checkoutBasket = (payload) => apiInstance.post('/shop/checkout', payload);
export const getMyOrders = () => apiInstance.get('/shop/orders/mine');
export const getOrder = (id) => apiInstance.get(`/shop/orders/${id}`);
export const settleOrder = (id) => apiInstance.post(`/shop/orders/${id}/settle`);

// Services and appointment requests. A request is not a booking until the
// business answers, and everything here is worded that way.
export const getShopServices = (slug) => apiInstance.get(`/shop/${slug}/services`);
export const requestAppointment = (data) => apiInstance.post('/appointments', data);
export const getMyAppointments = () => apiInstance.get('/appointments/mine');
export const cancelAppointment = (id) => apiInstance.post(`/appointments/${id}/cancel`);

export const getBusinessServices = (id) => apiInstance.get(`/businesses/${id}/services`);
export const addBusinessService = (id, data) => apiInstance.post(`/businesses/${id}/services`, data);
export const retireBusinessService = (id, serviceId) =>
  apiInstance.delete(`/businesses/${id}/services/${serviceId}`);
export const getBusinessAppointments = (id, status) =>
  apiInstance.get(`/businesses/${id}/appointments${status ? `?status=${status}` : ''}`);
export const respondToAppointment = (id, appointmentId, data) =>
  apiInstance.post(`/businesses/${id}/appointments/${appointmentId}/respond`, data);

// Quotes, and the customer records behind them.
export const getPublicQuoteForm = (slug) => apiInstance.get(`/shop/${slug}/quote-form`);
export const requestQuote = (data) => apiInstance.post('/quote-requests', data);
export const getMyQuoteRequests = () => apiInstance.get('/quote-requests/mine');
export const respondToQuote = (quoteId, data) => apiInstance.post(`/quotes/${quoteId}/respond`, data);

export const getQuoteForm = (id) => apiInstance.get(`/shop/${id}/quote-form`);
export const setQuoteForm = (id, questions) => apiInstance.put(`/businesses/${id}/quote-form`, { questions });
export const getBusinessQuoteRequests = (id) => apiInstance.get(`/businesses/${id}/quote-requests`);
export const markQuoteRequestViewed = (id, requestId) =>
  apiInstance.post(`/businesses/${id}/quote-requests/${requestId}/view`);
export const sendQuote = (id, requestId, data) =>
  apiInstance.post(`/businesses/${id}/quote-requests/${requestId}/quote`, data);

export const getBusinessCustomers = (id) => apiInstance.get(`/businesses/${id}/customers`);
export const getBusinessCustomer = (id, customerId) =>
  apiInstance.get(`/businesses/${id}/customers/${customerId}`);
export const addCustomerNote = (id, customerId, text) =>
  apiInstance.post(`/businesses/${id}/customers/${customerId}/notes`, { text });

// Reporting and blocking. A report creates work for a person and changes
// nothing else; a block takes effect immediately and needs nobody's approval.
export const getReportReasons = () => apiInstance.get('/moderation/reasons');
export const reportSomething = (data) => apiInstance.post('/moderation/reports', data);
export const blockMember = (userId) => apiInstance.post(`/moderation/blocks/${userId}`);
export const unblockMember = (userId) => apiInstance.delete(`/moderation/blocks/${userId}`);
export const getMyBlocks = () => apiInstance.get('/moderation/blocks');

// The review queue. Admins only, and the server says so rather than trusting
// this file to hide the buttons.
export const getModerationQueue = (status = 'open') =>
  apiInstance.get(`/admin/moderation/queue?status=${status}`);
export const getReportedSubject = (type, id) =>
  apiInstance.get(`/admin/moderation/subject/${type}/${id}`);
export const decideOnReport = (data) => apiInstance.post('/admin/moderation/decide', data);
export const suspendMember = (userId, reason) =>
  apiInstance.post(`/admin/moderation/members/${userId}/suspend`, { reason });
export const restoreMember = (userId) =>
  apiInstance.post(`/admin/moderation/members/${userId}/restore`);

// Recommendations. Not reviews: your name is on them, and a business can reply
// once but can never remove one.
export const getRecommendations = (businessId) =>
  apiInstance.get(`/businesses/${businessId}/recommendations`);
export const leaveRecommendation = (businessId, data) =>
  apiInstance.post(`/businesses/${businessId}/recommendations`, data);
export const withdrawRecommendation = (id) => apiInstance.delete(`/recommendations/${id}`);
export const replyToRecommendation = (id, body) =>
  apiInstance.post(`/recommendations/${id}/reply`, { body });
export const markRecommendationHelpful = (id) =>
  apiInstance.post(`/recommendations/${id}/helpful`);

// Events. A capacity, an RSVP and a waiting list - not a ticketing platform.
export const getEvents = (params = {}) =>
  apiInstance.get('/events', { params });
export const getMyEvents = () => apiInstance.get('/events/mine');
export const getEvent = (id) => apiInstance.get(`/events/${id}`);
export const createEvent = (data) => apiInstance.post('/events', data);
export const cancelEvent = (id, reason) => apiInstance.post(`/events/${id}/cancel`, { reason });
export const rsvpToEvent = (id) => apiInstance.post(`/events/${id}/rsvp`);
export const cancelRsvp = (id) => apiInstance.delete(`/events/${id}/rsvp`);
export const getEventAttendees = (id) => apiInstance.get(`/events/${id}/attendees`);

// Businesses in the feed, on the member's terms. Nothing here is settable by a
// business, and no amount of money changes what it stores.
export const postAsBusiness = (businessId, data) =>
  apiInstance.post(`/businesses/${businessId}/posts`, data);
export const getBusinessPosts = (businessId) =>
  apiInstance.get(`/businesses/${businessId}/posts`);
export const getBusinessFeed = () => apiInstance.get('/feed/businesses');
export const getFollowingState = (businessId) =>
  apiInstance.get(`/businesses/${businessId}/following`);
export const followBusiness = (businessId) =>
  apiInstance.post(`/businesses/${businessId}/follow`);
export const unfollowBusiness = (businessId) =>
  apiInstance.delete(`/businesses/${businessId}/follow`);
export const setContentPreferences = (businessId, kinds) =>
  apiInstance.put(`/businesses/${businessId}/content-preferences`, { kinds });
export const muteBusiness = (businessId) =>
  apiInstance.post(`/businesses/${businessId}/mute`);

// A business's own logo and header image. Sent as a file, not a URL - the
// server decides what a file actually is by reading its first bytes.
export const uploadBusinessImage = (businessId, kind, file) => {
  const body = new FormData();
  body.append('file', file);
  return apiInstance.post(`/businesses/${businessId}/${kind}`, body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const removeBusinessImage = (businessId, kind) =>
  apiInstance.delete(`/businesses/${businessId}/${kind}`);
